import { Grow, CircularProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import { useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Formik, Form } from 'formik';
import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { customerFactory } from 'v2/services/customer';

import type { ICardApiResponse } from 'v2/interfaces/customer';

import ListCards from './ListCards';
import NewCard from './newCard';
import PreviewCard from './previewCard';
import useStyles from './styles';
import Transition from './transition';
import CardSchema from './validations';
import DialogTitle from '../DialogTitle';

import type { FormikProps } from 'formik';

export interface ICard {
  id?: number;
  name: string;
  cvc: string;
  expiry: string;
  number: string;
  issuer?: string;
}

interface IProps {
  isOpen?: boolean;
  onClose?: () => void;
  onFinish?: (card: ICard) => void;
  cardsList: ICardApiResponse[];
  isLoadingCards: boolean;
  onCardClick?: (card: ICard) => void;
}

const steps = ['Selecione ou cadastre um novo cartão', 'Confirme seu cartão selecionado'];

export default function SelectCreditCard({ isOpen, onClose, onFinish, cardsList, isLoadingCards }: IProps) {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [newCard, setNewCard] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<ICard>();

  const handleNext = useCallback(() => {
    let newSkipped = skipped;

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  }, [skipped]);

  const handleBack = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }, []);

  const addNewCard = useCallback(() => {
    setNewCard(true);
  }, []);

  const backToList = useCallback(() => {
    setNewCard(false);
  }, []);

  const onClickCard = useCallback((card: ICard) => {
    setSelectedCard(card);
  }, []);

  const [onSubmit] = useCallbackObservable((values: ICard, { setSubmitting, resetForm }: FormikProps<ICard>) => {
    setSubmitting(true);
    return customerFactory.saveNewCard(values).pipe(
      tap((data) => {
        const card: ICard = { ...values, ...{ id: data.id, number: data.number, issuer: data.flag } };
        setSelectedCard(card);
        setSubmitting(false);
        setNewCard(false);
        resetForm();
        toast.success('Forma de pagamento cadastrada com sucesso!');
      }),
      catchError((err) => {
        setSubmitting(false);
        toast.error(err instanceof Error ? err.message : err);
        return of(true);
      })
    );
  }, []);

  const onComplete = useCallback(() => {
    onFinish(selectedCard);
    setActiveStep(0);
    setSelectedCard(null);
  }, [onFinish, selectedCard]);

  const onHandleClose = useCallback(() => {
    setActiveStep(0);
    setSelectedCard(null);
    onClose();
  }, [onClose]);

  return (
    <Formik validationSchema={CardSchema} initialValues={{ number: '', name: '', expiry: '', cvc: '' }} onSubmit={onSubmit}>
      {({ dirty, isValid, isSubmitting, submitForm }) => (
        <Form>
          <Dialog
            TransitionComponent={Transition}
            aria-labelledby='customized-dialog-title'
            open={isOpen}
            onClose={onClose}
            fullWidth={true}
            maxWidth={'lg'}
            fullScreen={fullScreen}
          >
            <DialogTitle id='customized-dialog-title' onClose={onHandleClose}>
              <Typography variant='h6'>Selecionar forma de pagamento</Typography>
              <Stepper activeStep={activeStep}>
                {steps.map((label: string) => {
                  const stepProps: { completed?: boolean } = {};
                  const labelProps: { optional?: React.ReactNode } = {};
                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </DialogTitle>
            <DialogContent className={classes.dialogContent} dividers>
              <div className={classes.root}>
                <>
                  {activeStep === 0 && (
                    <>
                      {!newCard && (
                        <Grow in>
                          <div>
                            <ListCards cardsList={cardsList} selectedCard={selectedCard} onCardClick={onClickCard} isLoadingCards={isLoadingCards} />
                          </div>
                        </Grow>
                      )}
                      {newCard && (
                        <Grow in>
                          <div>
                            <NewCard />
                          </div>
                        </Grow>
                      )}
                    </>
                  )}

                  {activeStep === 1 && selectedCard && (
                    <Typography className={classes.instructions}>
                      <PreviewCard card={selectedCard} />
                    </Typography>
                  )}
                </>
              </div>
            </DialogContent>
            <DialogActions disableSpacing={false}>
              <div>
                {activeStep === 0 && newCard ? (
                  <>
                    <Button onClick={backToList} className={classes.button}>
                      Voltar
                    </Button>
                    <Button
                      onClick={submitForm}
                      type='submit'
                      disabled={!dirty || !isValid || isSubmitting}
                      color='secondary'
                      variant='contained'
                      className={classes.button}
                    >
                      {isSubmitting && <CircularProgress size={10} style={{ color: '#192542', marginRight: 10 }} />}
                      Salvar Novo Cartão
                    </Button>
                  </>
                ) : (
                  <div>
                    {!newCard && activeStep === 0 && (
                      <Button color='secondary' variant='contained' className={classes.button} onClick={addNewCard}>
                        Adicionar novo cartão
                      </Button>
                    )}
                    {activeStep !== 0 && (
                      <Button onClick={handleBack} className={classes.button}>
                        Voltar
                      </Button>
                    )}
                    <Button
                      disabled={activeStep === 0 && !selectedCard}
                      variant='contained'
                      color='primary'
                      onClick={activeStep === steps.length - 1 ? onComplete : handleNext}
                      className={classes.button}
                    >
                      {activeStep === steps.length - 1 ? 'Confirmar' : 'Avançar'}
                    </Button>
                  </div>
                )}
              </div>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
}
